const getWindowValue = (path) => {
    if (typeof window === 'undefined') return undefined;

    return path.split('.').reduce((acc, key) => {
        if (acc === null || acc === undefined) return undefined;
        return acc[key];
    }, window);
};

const parseAmount = (amount) => {
    if (typeof amount === 'number') {
        return Number.isFinite(amount) ? amount : null;
    }

    if (typeof amount === 'string') {
        const parsed = parseFloat(amount);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const toCents = (amount) => {
    const numericAmount = parseAmount(amount);
    if (numericAmount === null) return null;
    return Math.round(numericAmount * 100);
};

export function parseFormattedMoney(value) {
    const numericText = String(value || '').replace(/[^\d.,-]/g, '');
    const decimalMatch = numericText.match(/[.,](\d{1,2})$/);
    const normalized = decimalMatch
        ? `${numericText.slice(0, decimalMatch.index).replace(/[.,]/g, '')}.${decimalMatch[1]}`
        : numericText.replace(/[.,]/g, '');
    return parseAmount(normalized);
}

export function getShopCurrencyCode() {
    return getWindowValue('Shopify.shop.currency') || getWindowValue('BOLD.common.Shopify.shop.currency') || 'GBP';
}

// Returns the buyer's country code (e.g. "US") from Shopify Markets, or null.
// Used to build @inContext(country:) directives for Storefront API queries
// so prices are returned in the market-resolved presentment currency.
export function getCountryCode() {
    const country = getWindowValue('Shopify.country');
    return typeof country === 'string' && country.length >= 2 ? country.toUpperCase() : null;
}

export function getActiveCurrencyCode() {
    if (typeof window !== 'undefined') {
        try {
            const selected = JSON.parse(window.localStorage.getItem('cbb-currency-converter-currency'));
            if (typeof selected?.value === 'string' && /^[a-z]{3}$/i.test(selected.value)) {
                return selected.value.toUpperCase();
            }
        } catch {
            return getWindowValue('Shopify.currency.active') || getShopCurrencyCode();
        }
    }

    return (
        getWindowValue('Shopify.currency.active') ||
        getWindowValue('BOLDCURRENCY.currentCurrency') ||
        getWindowValue('Currency.currentCurrency') ||
        getShopCurrencyCode()
    );
}

function getMoneyFormatForCurrency(currencyCode) {
    if (!currencyCode) return null;

    const boldMoneyFormat = getWindowValue(`BOLDCURRENCY.moneyFormats.${currencyCode}.money_format`);
    if (typeof boldMoneyFormat === 'string' && boldMoneyFormat.length > 0) {
        return boldMoneyFormat;
    }

    const legacyCurrencyFormat = getWindowValue(
        `Currency.moneyFormats.${currencyCode}.${getWindowValue('Currency.format')}`
    );
    if (typeof legacyCurrencyFormat === 'string' && legacyCurrencyFormat.length > 0) {
        return legacyCurrencyFormat;
    }

    return null;
}

/**
 * Format cents using a plain formatter that does NOT do currency conversion.
 * Falls back to Intl.NumberFormat if nothing is available.
 */
function formatMoneyPlain(centsAmount, moneyFormat, currencyCode) {
    // Shopify.formatMoney is a pure formatter (no conversion)
    const shopifyFormat = getWindowValue('Shopify.formatMoney');
    if (typeof shopifyFormat === 'function' && moneyFormat) {
        return shopifyFormat(centsAmount, moneyFormat);
    }

    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode || 'GBP',
    }).format(centsAmount / 100);
}

// Keep source components until display time. A cached build must never cache a
// conversion based on a rate that can change independently of React/Redux.
export function getMoneyComponents(money, sourceCurrencyCode = getShopCurrencyCode()) {
    if (money && typeof money === 'object') {
        if (Array.isArray(money.components)) return money.components;
        return [{ amount: money.amount, currencyCode: money.currencyCode || sourceCurrencyCode }];
    }
    return [{ amount: money, currencyCode: sourceCurrencyCode }];
}

export function getMoneyDisplay(money, sourceCurrencyCode = getShopCurrencyCode()) {
    const components = getMoneyComponents(money, sourceCurrencyCode);
    const activeCurrency = getActiveCurrencyCode();
    const shopCurrency = getShopCurrencyCode();
    const sumInCurrency = (currencyCode) => {
        let cents = 0;
        for (const component of components) {
            const converted = convertCurrencyAmount(component.amount, component.currencyCode, currencyCode);
            if (converted === null) return null;
            // Each product/option is a displayed line; totals sum those rounded lines.
            cents += toCents(converted);
        }
        return cents;
    };
    const format = (cents, currencyCode) => formatMoneyPlain(cents, getMoneyFormatForCurrency(currencyCode), currencyCode);
    const nativeCents = sumInCurrency(activeCurrency);
    if (nativeCents !== null) {
        return { html: format(nativeCents, activeCurrency), allowCbb: false };
    }

    // CBB expects a shop-currency seed. Only hand it a value we can actually
    // express in shop currency; otherwise show the source-currency subtotals.
    const shopCents = sumInCurrency(shopCurrency);
    if (shopCents !== null) {
        return { html: format(shopCents, shopCurrency), allowCbb: true };
    }
    const subtotals = new Map();
    for (const component of components) {
        const cents = toCents(component.amount);
        if (cents === null) return { html: '', allowCbb: false };
        const code = component.currencyCode;
        subtotals.set(code, (subtotals.get(code) || 0) + cents);
    }
    return {
        html: [...subtotals].map(([code, cents]) => format(cents, code)).join(' + '),
        allowCbb: false,
    };
}

export function getCurrencySignature(money = 1, sourceCurrencyCode = getShopCurrencyCode()) {
    const display = getMoneyDisplay(money, sourceCurrencyCode);
    // Includes conversion readiness and effective rates from Currency.convert,
    // even when Shopify.currency.rate and the selected currency stay unchanged.
    return `${getActiveCurrencyCode()}|${display.allowCbb}|${display.html}`;
}

export function formatBuilderMoney(money, sourceCurrencyCode = getShopCurrencyCode()) {
    return getMoneyDisplay(money, sourceCurrencyCode).html;
}

export function convertCurrencyAmount(amount, sourceCurrencyCode = getShopCurrencyCode(), targetCurrencyCode = getActiveCurrencyCode()) {
    const numericAmount = parseAmount(amount);
    if (numericAmount === null || sourceCurrencyCode === targetCurrencyCode) return numericAmount;

    const currencyConvert = getWindowValue('Currency.convert');
    if (typeof currencyConvert === 'function') {
        try {
            const converted = currencyConvert(numericAmount, sourceCurrencyCode, targetCurrencyCode);
            if (Number.isFinite(converted)) return converted;
        } catch {
            // A delayed converter may exist before its rates have loaded.
        }
    }

    const marketCurrency = getWindowValue('Shopify.currency.active');
    const shopCurrency = getShopCurrencyCode();
    const rate = parseFloat(getWindowValue('Shopify.currency.rate'));
    if (Number.isFinite(rate) && rate > 0) {
        if (sourceCurrencyCode === shopCurrency && targetCurrencyCode === marketCurrency) return numericAmount * rate;
        if (sourceCurrencyCode === marketCurrency && targetCurrencyCode === shopCurrency) return numericAmount / rate;
    }

    return null;
}

export function syncMoneyDisplay(target, sourceCurrencyCode = getShopCurrencyCode(), allowCbb = getMoneyDisplay(1, sourceCurrencyCode).allowCbb) {
    const wrapper = target.nextElementSibling;
    const isWrapper = wrapper?.classList?.contains('currency-converter-wrapper-amount-box');
    const targetDisplay = !allowCbb || !isWrapper ? 'inline' : 'none';
    if (target.style.getPropertyValue('display') !== targetDisplay || target.style.getPropertyPriority('display') !== 'important') {
        target.style.setProperty('display', targetDisplay, 'important');
    }
    const wrapperDisplay = allowCbb ? 'inline-block' : 'none';
    if (isWrapper && (wrapper.style.getPropertyValue('display') !== wrapperDisplay || wrapper.style.getPropertyPriority('display') !== 'important')) {
        wrapper.style.setProperty('display', wrapperDisplay, 'important');
    }
}

// Convert a price from shop base currency (GBP) to the active presentment currency.
// Use this ONLY for values that are always stored in shop currency (e.g. Bold addon prices in GBP cents).
export function convertFromShopCurrency(centsAmountInShopCurrency) {
    if (!Number.isFinite(centsAmountInShopCurrency)) return 0;
    const converted = convertCurrencyAmount(centsAmountInShopCurrency);
    return converted === null ? centsAmountInShopCurrency : Math.round(converted);
}
